import { Badge } from "../ui/badge";
import { ListChecks, AlertTriangle, CheckCircle2, Clock, PauseCircle } from "lucide-react";

/**
 * Per-row task count badge for list views (Spec Section 6).
 * Receives pre-fetched counts from the parent (to avoid N+1 API calls).
 *
 * Usage: parent fetches tasksAPI.getCountsBatch(module) once, then passes
 * the count object for each row: <TasksCountBadge counts={countsMap[record.id]} />
 *
 * Props:
 *  - showStatusInline: when true AND there's exactly one linked task, render the
 *    task's status ("Pending" / "In progress" / "Blocked" / "Complete") instead
 *    of a count. Used on DSR list per spec ("DSR shows task status inline").
 */
export function TasksCountBadge({ counts, onClick, showStatusInline = false }) {
  if (!counts || counts.total === 0) return null;

  const hasOverdue = counts.overdue > 0;
  const title = `${counts.open} open · ${counts.overdue} overdue · ${counts.completed} completed`;

  // Status-inline mode: exactly one task → show its status directly
  if (showStatusInline && counts.total === 1) {
    const { variant, icon: Icon, label } = resolveSingleStatus(counts, hasOverdue);
    return (
      <div
        className={`inline-flex items-center gap-1 ${onClick ? "cursor-pointer hover:opacity-80" : ""}`}
        onClick={onClick}
        title={title}
      >
        <Badge variant={variant} className="text-xs gap-1">
          <Icon className="h-3 w-3" />
          {label}
        </Badge>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1 ${onClick ? "cursor-pointer hover:opacity-80" : ""}`}
      onClick={onClick}
      title={title}
    >
      <Badge variant={hasOverdue ? "destructive" : "secondary"} className="text-xs gap-1">
        {hasOverdue ? <AlertTriangle className="h-3 w-3" /> : <ListChecks className="h-3 w-3" />}
        {counts.open > 0 ? `${counts.open} open` : `${counts.total} task${counts.total === 1 ? "" : "s"}`}
      </Badge>
    </div>
  );
}

function resolveSingleStatus(counts, hasOverdue) {
  if (counts.completed === 1) {
    return { variant: "success", icon: CheckCircle2, label: "Complete" };
  }
  if (hasOverdue) {
    return { variant: "destructive", icon: AlertTriangle, label: "Overdue" };
  }
  if (counts.in_progress === 1) {
    return { variant: "default", icon: Clock, label: "In progress" };
  }
  if (counts.blocked === 1) {
    return { variant: "destructive", icon: PauseCircle, label: "Blocked" };
  }
  return { variant: "secondary", icon: ListChecks, label: "Pending" };
}

export default TasksCountBadge;
